import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, User, Building2, Calendar, Award,
  CheckCircle, XCircle, AlertCircle, Search, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { UserContext } from '../../contexts/UserContext';
import api from '../../services/apiService';

const OEMNominationCreate = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    project: '',
    authorization_level: '',
    user: '',
    date_start: '',
    date_end: '',
    comment: ''
  });

  // Reference data
  const [projects, setProjects] = useState([]);
  const [authorizationLevels, setAuthorizationLevels] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [technicianSearch, setTechnicianSearch] = useState('');
  const [showTechnicianDropdown, setShowTechnicianDropdown] = useState(false);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await api.get('/oem/projects/');
        // Filter only active projects
        const activeProjects = response.data.filter(p => p.status === 'active');
        setProjects(activeProjects);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
      }
    };
    fetchProjects();
  }, []);

  // Fetch authorization levels when project changes
  useEffect(() => {
    const fetchAuthorizationLevels = async () => {
      if (!formData.project) {
        setAuthorizationLevels([]);
        return;
      }
      try {
        const response = await api.get(`/oem/authorization-levels/?project=${formData.project}`);
        setAuthorizationLevels(response.data);
      } catch (err) {
        console.error('Error fetching authorization levels:', err);
      }
    };
    fetchAuthorizationLevels();
  }, [formData.project]);

  // Fetch technicians when project changes (users from participating companies)
  useEffect(() => {
    const fetchTechnicians = async () => {
      if (!formData.project) {
        setTechnicians([]);
        return;
      }
      try {
        // Get project companies first
        const companiesResponse = await api.get(`/oem/project-companies/?project=${formData.project}`);
        const companyIds = companiesResponse.data
          .filter(c => c.status === 'active')
          .map(c => c.company);

        // Get users from those companies
        if (companyIds.length > 0) {
          const usersResponse = await api.get('/users/', {
            params: { company__in: companyIds.join(',') }
          });
          setTechnicians(usersResponse.data);
        }
      } catch (err) {
        console.error('Error fetching technicians:', err);
      }
    };
    fetchTechnicians();
  }, [formData.project]);

  // Filter technicians based on search
  const filteredTechnicians = technicians.filter(tech => {
    if (!technicianSearch) return true;
    const searchLower = technicianSearch.toLowerCase();
    const fullName = `${tech.first_name} ${tech.last_name}`.toLowerCase();
    return fullName.includes(searchLower) ||
           tech.email?.toLowerCase().includes(searchLower) ||
           tech.username?.toLowerCase().includes(searchLower);
  });

  // Handle project selection
  const handleProjectChange = (projectId) => {
    const project = projects.find(p => p.id === parseInt(projectId));
    setSelectedProject(project);
    setFormData(prev => ({
      ...prev,
      project: projectId,
      authorization_level: '',
      user: ''
    }));
    setSelectedLevel(null);
    setSelectedTechnician(null);
  };

  // Handle authorization level selection
  const handleLevelChange = (levelId) => {
    const level = authorizationLevels.find(l => l.id === parseInt(levelId));
    setSelectedLevel(level);
    setFormData(prev => ({
      ...prev,
      authorization_level: levelId
    }));
  };

  // Handle technician selection
  const handleTechnicianSelect = (tech) => {
    setSelectedTechnician(tech);
    setFormData(prev => ({
      ...prev,
      user: tech.id
    }));
    setTechnicianSearch(`${tech.first_name} ${tech.last_name}`);
    setShowTechnicianDropdown(false);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!formData.project || !formData.authorization_level || !formData.user) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (!formData.date_start || !formData.date_end) {
      setError('Please select validity dates');
      setLoading(false);
      return;
    }

    if (new Date(formData.date_end) < new Date(formData.date_start)) {
      setError('End date must be after start date');
      setLoading(false);
      return;
    }

    try {
      await api.post('/oem/nominations/', formData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/oem/nominations');
      }, 1500);
    } catch (err) {
      console.error('Error creating nomination:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to create nomination');
    } finally {
      setLoading(false);
    }
  };

  // Check if technician meets requirements
  const checkRequirements = () => {
    if (!selectedTechnician || !selectedLevel) return null;

    const userQualIds = selectedTechnician.qualifications?.map(q => q.qualification_type) || [];
    const requiredDocIds = selectedLevel.required_documents || [];

    const missingDocs = requiredDocIds.filter(docId => !userQualIds.includes(docId));
    const hasMissing = missingDocs.length > 0;

    return {
      meetsRequirements: !hasMissing,
      missingDocs
    };
  };

  const requirementCheck = checkRequirements();

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/oem/nominations')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Nomination</h1>
          <p className="text-gray-500">Nominate a technician for an authorization level</p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          Nomination created successfully! Redirecting...
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
          <XCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Project Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="h-5 w-5" />
              Project Selection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              {/* Project */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project *
                </label>
                <select
                  value={formData.project}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a project...</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.code} - {project.name}
                    </option>
                  ))}
                </select>
                {selectedProject && (
                  <div className="mt-2 text-sm text-gray-500">
                    Location: {selectedProject.location || 'N/A'}
                  </div>
                )}
              </div>

              {/* Authorization Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Authorization Level *
                </label>
                <select
                  value={formData.authorization_level}
                  onChange={(e) => handleLevelChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={!formData.project}
                >
                  <option value="">Select an authorization level...</option>
                  {authorizationLevels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.code} - {level.name}
                    </option>
                  ))}
                </select>
                {selectedLevel && (
                  <div className="mt-2 text-sm text-gray-500">
                    {selectedLevel.description || 'No description'}
                  </div>
                )}
              </div>
            </div>

            {/* Level Permissions */}
            {selectedLevel && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Level Permissions</h4>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={selectedLevel.can_work_unsupervised} disabled className="rounded" />
                    Can work unsupervised
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={selectedLevel.can_supervise_others} disabled className="rounded" />
                    Can supervise others
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={selectedLevel.can_approve_work} disabled className="rounded" />
                    Can approve work
                  </label>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Technician Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Technician Selection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Technician *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={technicianSearch}
                  onChange={(e) => {
                    setTechnicianSearch(e.target.value);
                    setShowTechnicianDropdown(true);
                  }}
                  onFocus={() => setShowTechnicianDropdown(true)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-10 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!formData.project}
                />
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>

              {/* Dropdown */}
              {showTechnicianDropdown && formData.project && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredTechnicians.length === 0 ? (
                    <div className="px-4 py-3 text-gray-500 text-sm">No technicians found</div>
                  ) : (
                    filteredTechnicians.map((tech) => (
                      <div
                        key={tech.id}
                        onClick={() => handleTechnicianSelect(tech)}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                      >
                        <div className="font-medium text-gray-900">
                          {tech.first_name} {tech.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{tech.email}</div>
                        <div className="text-xs text-gray-400">
                          {tech.company_name || 'No company'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Selected Technician Info */}
            {selectedTechnician && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {selectedTechnician.first_name} {selectedTechnician.last_name}
                    </h4>
                    <p className="text-sm text-gray-600">{selectedTechnician.email}</p>
                    <p className="text-sm text-gray-500">{selectedTechnician.company_name}</p>
                  </div>
                  {requirementCheck && (
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      requirementCheck.meetsRequirements
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {requirementCheck.meetsRequirements ? 'Meets Requirements' : 'Missing Documents'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Requirements Warning */}
            {requirementCheck && !requirementCheck.meetsRequirements && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Missing Required Documents</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      This technician is missing some required documents for this authorization level.
                      The nomination can still be created but may be rejected during review.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Validity Period */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Validity Period
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.date_start}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    let endDate = '';
                    if (startDate) {
                      const start = new Date(startDate);
                      start.setFullYear(start.getFullYear() + 3);
                      endDate = start.toISOString().split('T')[0];
                    }
                    setFormData(prev => ({ ...prev, date_start: startDate, date_end: endDate }));
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.date_end}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_end: e.target.value }))}
                  min={formData.date_start || new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comments */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Additional Comments</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Add any additional comments or notes..."
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/oem/nominations')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || success}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Create Nomination
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OEMNominationCreate;
